import React, { useState, useEffect } from 'react'
import {X, DollarSign, Calendar, Clock, FileText, CheckCircle, AlertCircle, AlertTriangle, Wallet, Copy, Trash2, User} from 'lucide-react'
import { Task } from '@/hooks/useTasks'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { DisputeModal } from './DisputeModal'
import { AgentProfileModal } from './AgentProfileModal'

interface TaskDetailModalProps {
  task: Task
  isOpen: boolean
  onClose: () => void
  onTaskUpdated: () => void
  currentUserId?: string
}

export function TaskDetailModal({ task, isOpen, onClose, onTaskUpdated, currentUserId }: TaskDetailModalProps) {
  const [claiming, setClaiming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [output, setOutput] = useState('')
  const [showDispute, setShowDispute] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [posterAgent, setPosterAgent] = useState<any>(null)
  const [claimingAgent, setClaimingAgent] = useState<any>(null)
  const [selectedAgent, setSelectedAgent] = useState<any>(null)

  useEffect(() => {
    if (isOpen && task) {
      fetchPosterAgent()
      fetchClaimingAgent()
    }
  }, [isOpen, task])

  const fetchPosterAgent = async () => {
    const agentId = task.postedBy || task.requesterId
    if (!agentId) return
    
    try {
      const { list } = await lumi.entities.agents.list({
        filter: { _id: agentId },
        limit: 1
      })
      const agent = list?.[0]
      if (agent && agent.availability !== 'deleted' && !agent.name?.startsWith('[DELETED]')) {
        setPosterAgent(agent)
      }
    } catch (error) {
      console.error('Failed to fetch poster agent:', error)
    }
  }

  const fetchClaimingAgent = async () => {
    if (!task.agentId) return
    
    try {
      const { list } = await lumi.entities.agents.list({
        filter: { _id: task.agentId },
        limit: 1
      })
      const agent = list?.[0]
      if (agent && agent.availability !== 'deleted' && !agent.name?.startsWith('[DELETED]')) {
        setClaimingAgent(agent)
      }
    } catch (error) {
      console.error('Failed to fetch claiming agent:', error)
    }
  }

  if (!isOpen) return null

  const canClaim = task.status === 'created' && currentUserId
  const taskCreatorId = task.postedBy || task.requesterId
  const needsPayment = task.status === 'claimed' && taskCreatorId === currentUserId && !task.paymentConfirmed
  const canConfirmPayment = task.status === 'claimed' && task.agentId === currentUserId && !task.paymentConfirmed
  const canSubmit = task.status === 'claimed' && task.agentId === currentUserId && task.paymentConfirmed
  const isRequester = taskCreatorId === currentUserId
  const canDelete = isRequester && ['created', 'open'].includes(task.status)

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const handleConfirmPayment = async () => {
    try {
      await lumi.entities.tasks.update(task._id!, {
        paymentConfirmed: true,
        updatedAt: new Date().toISOString()
      })
      toast.success('Payment confirmed! You can now complete the task.')
      onTaskUpdated()
    } catch (error) {
      console.error('Confirm payment failed:', error)
      toast.error('Failed to confirm payment')
    }
  }

  const handleClaim = async () => {
    if (!currentUserId) {
      toast.error('Please login to claim tasks')
      return
    }

    setClaiming(true)
    try {
      await lumi.entities.tasks.update(task._id!, {
        status: 'claimed',
        agentId: currentUserId,
        updatedAt: new Date().toISOString()
      })
      toast.success('Task claimed successfully!')
      onTaskUpdated()
      onClose()
    } catch (error) {
      console.error('Claim failed:', error)
      toast.error('Failed to claim task')
    } finally {
      setClaiming(false)
    }
  }

  const handleSubmit = async () => {
    if (!output.trim()) {
      toast.error('Please provide output')
      return
    }

    setSubmitting(true)
    try {
      await lumi.entities.tasks.update(task._id!, {
        status: 'submitted',
        submittedOutput: output,
        updatedAt: new Date().toISOString()
      })
      toast.success('Task submitted successfully!')
      onTaskUpdated()
      onClose()
    } catch (error) {
      console.error('Submit failed:', error)
      toast.error('Failed to submit task')
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async () => {
    try {
      await lumi.entities.tasks.update(task._id!, {
        status: 'completed',
        updatedAt: new Date().toISOString()
      })
      toast.success('Task completed!')
      onTaskUpdated()
      onClose()
    } catch (error) {
      console.error('Verify failed:', error)
      toast.error('Failed to verify task')
    }
  }

  const handleDelete = async () => {
    try {
      await lumi.entities.tasks.update(task._id!, {
        status: 'deleted',
        updatedAt: new Date().toISOString()
      })
      toast.success('Task deleted successfully')
      setShowDeleteConfirm(false)
      onTaskUpdated()
      onClose()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('Failed to delete task')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      created: 'bg-electric-purple-500/20 text-electric-purple-400 border-electric-purple-500/50',
      claimed: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      submitted: 'bg-electric-purple-500/20 text-electric-purple-400 border-electric-purple-500/50',
      completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
      disputed: 'bg-red-500/20 text-red-400 border-red-500/50'
    }
    return colors[status] || 'bg-slate-500/20 text-slate-400 border-slate-500/50'
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black border-b border-gray-800 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{task.title}</h2>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(task.status)}`}>
                {task.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Posted By Section */}
          {posterAgent && (
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 mb-4">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <User className="w-4 h-4" />
                <span className="text-sm">Posted by:</span>
              </div>
              <button
                onClick={() => setSelectedAgent(posterAgent)}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                {posterAgent.profilePicture && (
                  <img 
                    src={posterAgent.profilePicture} 
                    alt={posterAgent.name} 
                    className="w-10 h-10 rounded-full object-cover border border-purple-500"
                  />
                )}
                <span className="text-lg font-semibold text-purple-400 hover:underline">
                  {posterAgent.name}
                </span>
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Budget</span>
              </div>
              <p className="text-2xl font-bold text-white">{task.budget.toLocaleString('en-US')} {task.currency || 'XYBER'}</p>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-2 text-slate-400 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Deadline</span>
              </div>
              <p className="text-lg font-semibold text-white">{formatDate(task.deadline)}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
              <p className="text-slate-300 leading-relaxed">{task.description}</p>
            </div>

            {task.requirements && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Requirements</h3>
                <p className="text-slate-300 leading-relaxed">{task.requirements}</p>
              </div>
            )}

            {task.expectedOutput && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Expected Output</h3>
                <p className="text-slate-300 leading-relaxed">{task.expectedOutput}</p>
              </div>
            )}

            <div className="flex gap-4">
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <span className="text-sm text-slate-400">Category: </span>
                <span className="text-purple-400 font-semibold">{task.category}</span>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <span className="text-sm text-slate-400">Type: </span>
                <span className="text-purple-300 font-semibold">{task.taskType?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>

          {/* Payment Instructions for Requester */}
          {needsPayment && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                ⚠️ Payment Required: Task Claimed
              </h3>
              <p className="text-yellow-200 text-sm mb-4">
                An agent has claimed your task. Please send the following payments to proceed:
              </p>
              
              <div className="space-y-3">
                <div className="bg-black/50 border border-yellow-900 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-300 mb-2">Payment 1: Task Budget in $Xyber</p>
                  <p className="text-xs text-gray-500 mb-2">Amount: {task.budget.toLocaleString('en-US')} {task.currency || 'XYBER'} tokens</p>
                  <p className="text-xs text-gray-500 mb-2">Send to Agent Wallet:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-deep-purple-950 px-3 py-2 rounded text-electric-purple-500 text-xs break-all">
                      {claimingAgent?.walletAddress || 'Loading agent wallet...'}
                    </code>
                    <button
                      onClick={() => copyToClipboard(claimingAgent?.walletAddress || '', 'agent')}
                      disabled={!claimingAgent?.walletAddress}
                      className="p-2 hover:bg-deep-purple-900 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy className={`w-4 h-4 ${copiedField === 'agent' ? 'text-green-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 mb-2">$Xyber Token Contract (Base):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-deep-purple-950 px-3 py-2 rounded text-electric-purple-500 text-xs break-all">
                      0x502Ac1109164DF54323fCFC4b2631EAFD1A66b07
                    </code>
                    <button
                      onClick={() => copyToClipboard('0x502Ac1109164DF54323fCFC4b2631EAFD1A66b07', 'xyber')}
                      className="p-2 hover:bg-deep-purple-900 rounded transition-colors"
                    >
                      <Copy className={`w-4 h-4 ${copiedField === 'xyber' ? 'text-green-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                </div>

                <div className="bg-black/50 border border-yellow-900 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-300 mb-2">Payment 2: Platform Fee (Flat $3 USDC)</p>
                  <p className="text-xs text-gray-500 mb-2">Send to Platform Wallet:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-deep-purple-950 px-3 py-2 rounded text-electric-purple-500 text-xs break-all">
                      0xfE20F68FA939AAc47617CF39940688E3c0167FB5
                    </code>
                    <button
                      onClick={() => copyToClipboard('0xfE20F68FA939AAc47617CF39940688E3c0167FB5', 'platform')}
                      className="p-2 hover:bg-deep-purple-900 rounded transition-colors"
                    >
                      <Copy className={`w-4 h-4 ${copiedField === 'platform' ? 'text-green-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 mb-2">USDC Token Contract (Base):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-deep-purple-950 px-3 py-2 rounded text-electric-purple-500 text-xs break-all">
                      0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913
                    </code>
                    <button
                      onClick={() => copyToClipboard('0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', 'usdc')}
                      className="p-2 hover:bg-deep-purple-900 rounded transition-colors"
                    >
                      <Copy className={`w-4 h-4 ${copiedField === 'usdc' ? 'text-green-500' : 'text-gray-400'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-yellow-400 mt-3">
                ⚠️ Both payments must be sent on Base blockchain (Chain ID: 8453). The agent will confirm receipt before starting work.
              </p>
            </div>
          )}

          {/* Payment Confirmation for Claiming Agent */}
          {canConfirmPayment && (
            <div className="bg-electric-purple-500/10 border border-electric-purple-500/30 rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-electric-purple-400 mb-3 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                💰 Confirm Payment Receipt
              </h3>
              <p className="text-white text-sm mb-4">
                The task requester should have sent you {task.budget.toLocaleString('en-US')} {task.currency || 'XYBER'} tokens. Once you've confirmed receipt in your wallet, click below to start work.
              </p>
              <button
                onClick={handleConfirmPayment}
                className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-emerald-500/50 transition-all flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirm Payment Received - Start Task
              </button>
            </div>
          )}

          {task.status === 'submitted' && task.submittedOutput && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Submitted Output
              </h3>
              <p className="text-slate-300">{task.submittedOutput}</p>
            </div>
          )}

          {canSubmit && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Submit Your Work</h3>
              <textarea
                value={output}
                onChange={(e) => setOutput(e.target.value)}
                placeholder="Provide your deliverable output here..."
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none h-32"
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            {canDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all flex items-center justify-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Delete Task
              </button>
            )}

            {canClaim && (
              <button
                onClick={handleClaim}
                disabled={claiming}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {claiming ? 'Claiming...' : 'Claim Task'}
              </button>
            )}

            {canSubmit && (
              <button
                onClick={handleSubmit}
                disabled={submitting || !output.trim()}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Work'}
              </button>
            )}

            {isRequester && task.status === 'submitted' && (
              <>
                <button
                  onClick={handleVerify}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-emerald-500/50 transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Verify & Complete
                </button>
                <button
                  onClick={() => setShowDispute(true)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all flex items-center justify-center gap-2"
                >
                  <AlertTriangle className="w-5 h-5" />
                  Dispute
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <DisputeModal
        task={task}
        isOpen={showDispute}
        onClose={() => setShowDispute(false)}
        onSuccess={() => {
          setShowDispute(false)
          onTaskUpdated()
          onClose()
        }}
        userId={currentUserId}
      />

      {/* Agent Profile Modal */}
      {selectedAgent && (
        <AgentProfileModal
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-black border-2 border-red-500 rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <h3 className="text-xl font-bold text-white">Delete Task?</h3>
            </div>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this task? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
